/* eslint-disable no-empty */
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../gate33-program/g33.css';
import { ProgramProvider, useProgram } from '../gate33-program/ProgramContext';
import PasswordGate from '../gate33-program/PasswordGate';
import { CATERING_REF_STEPS, CATERING_REF_HERO_IMG } from './catering-steps';

// =====================================================================
// /gate33-catering — private one-page reference for Catering & Operations
// Uses the same password mechanism as /gate33-program (shared context).
// Not linked publicly. noindex, nofollow.
// =====================================================================

// Inline **bold** rendering
const renderInline = (text) => {
  const parts = String(text).split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith('**') && p.endsWith('**')) return <strong key={i}>{p.slice(2, -2)}</strong>;
    if (p.startsWith('*') && p.endsWith('*') && p.length > 2)
      return <em key={i}>{p.slice(1, -1)}</em>;
    return <span key={i}>{p}</span>;
  });
};

const StepBlock = ({ step, index, total }) => (
  <section
    data-testid={`catering-ref-step-${step.n}`}
    id={`step-${step.n}`}
    style={{
      borderTop: index === 0 ? 'none' : '1px solid var(--g33-ligne)',
      padding: '96px 0',
    }}
  >
    <div style={{ maxWidth: 860, margin: '0 auto', padding: '0 24px' }}>
      {/* Numeral + title header */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 24, marginBottom: 8 }}>
        <span className="g33-mega-numeral" style={{ fontSize: 96, lineHeight: 1 }}>
          {step.n}
        </span>
        <span className="g33-eyebrow">
          Step {step.n} of {total} · {step.duration}
        </span>
      </div>

      <h2 className="g33-display" style={{ fontSize: 44, marginTop: 0, marginBottom: 12 }}>
        {step.title}
      </h2>
      <p
        className="g33-body"
        style={{
          fontStyle: 'italic',
          color: 'var(--g33-grey-warm)',
          marginBottom: 48,
          fontSize: 18,
        }}
      >
        {step.tagline}
      </p>

      {/* Step image (when present) */}
      {step.image && (
        <figure
          data-testid={`catering-ref-step-${step.n}-image`}
          style={{ margin: '0 0 48px', padding: 0 }}
        >
          <div
            style={{
              width: '100%',
              aspectRatio: '16 / 9',
              overflow: 'hidden',
              border: '1px solid var(--g33-ligne)',
              background: 'var(--g33-cream-soft)',
            }}
          >
            <img
              src={step.image}
              alt={`Step ${step.n} — ${step.title}`}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              loading="lazy"
            />
          </div>
          {step.imageCaption && (
            <figcaption
              className="g33-body"
              style={{
                fontSize: 13,
                color: 'var(--g33-grey-warm)',
                fontStyle: 'italic',
                marginTop: 12,
                paddingLeft: 12,
                borderLeft: '2px solid var(--g33-or)',
              }}
            >
              {step.imageCaption}
            </figcaption>
          )}
        </figure>
      )}

      {/* Objective */}
      <div style={{ marginBottom: 40 }}>
        <div className="g33-eyebrow" style={{ marginBottom: 10 }}>Objective</div>
        <p className="g33-body" style={{ fontSize: 17 }}>{renderInline(step.objective)}</p>
      </div>

      {/* Trainer narrative */}
      <div style={{ marginBottom: 40 }}>
        <div className="g33-eyebrow" style={{ marginBottom: 10 }}>Trainer narrative</div>
        {step.narrative.map((para, i) => (
          <p key={i} className="g33-body" style={{ fontSize: 17, marginBottom: 18 }}>
            {renderInline(para)}
          </p>
        ))}
      </div>

      {/* Operational insight (pull quote) */}
      <blockquote
        className="g33-pull-quote"
        style={{
          margin: '0 0 40px',
          padding: '28px 32px',
          borderLeft: '2px solid var(--g33-or)',
          background: 'var(--g33-cream-soft)',
          fontStyle: 'italic',
        }}
      >
        {renderInline(step.insight)}
      </blockquote>

      {/* Classroom exercise — prominent card */}
      <div
        data-testid={`catering-ref-step-${step.n}-exercise`}
        style={{
          margin: '0 0 40px',
          border: '1px solid var(--g33-noir)',
          background: 'var(--g33-noir)',
          color: 'var(--g33-cream)',
          padding: '32px 36px',
          position: 'relative',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            marginBottom: 18,
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 32,
              height: 32,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid var(--g33-or)',
              fontFamily: 'Playfair Display, serif',
              fontSize: 14,
              color: 'var(--g33-or)',
              fontStyle: 'italic',
            }}
          >
            §
          </span>
          <span
            className="g33-eyebrow"
            style={{ color: 'var(--g33-or)', fontSize: 12, letterSpacing: '0.24em' }}
          >
            Classroom exercise · Live oral
          </span>
        </div>
        <p
          className="g33-body"
          style={{
            fontSize: 17,
            color: 'var(--g33-cream)',
            marginBottom: 0,
          }}
        >
          {renderInline(step.participant)}
        </p>
      </div>

      {/* Demonstration */}
      <div style={{ marginBottom: 40 }}>
        <div className="g33-eyebrow" style={{ marginBottom: 10 }}>Demonstration</div>
        <p className="g33-body" style={{ fontSize: 17 }}>{renderInline(step.demonstration)}</p>
      </div>

      {/* Key takeaways */}
      <div>
        <div className="g33-eyebrow" style={{ marginBottom: 10 }}>Key takeaways</div>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {step.takeaways.map((t, i) => (
            <li
              key={i}
              className="g33-body"
              style={{
                fontSize: 16,
                padding: '10px 0 10px 24px',
                position: 'relative',
                borderBottom: i === step.takeaways.length - 1 ? 'none' : '1px solid var(--g33-ligne)',
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 18,
                  width: 8,
                  height: 8,
                  background: 'var(--g33-or)',
                }}
              />
              {renderInline(t)}
            </li>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

const Landing = () => {
  const { logout } = useProgram();
  const total = CATERING_REF_STEPS.length;

  return (
    <div className="g33-program" data-testid="gate33-catering-ref">
      {/* Simple top bar */}
      <div
        style={{
          borderBottom: '1px solid var(--g33-ligne)',
          padding: '20px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'var(--g33-cream)',
        }}
      >
        <div className="g33-eyebrow">Gate 33 · Confidential · Catering & Ops Reference</div>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          <Link
            to="/gate33-program"
            className="g33-btn-ghost"
            style={{ fontSize: 11 }}
            data-testid="catering-ref-to-program"
          >
            Go to 3-Day Program
          </Link>
          <button
            type="button"
            onClick={logout}
            className="g33-btn-ghost"
            style={{ fontSize: 11 }}
            data-testid="catering-ref-logout"
          >
            Sign out
          </button>
        </div>
      </div>

      {/* Hero */}
      <section
        style={{
          position: 'relative',
          padding: '120px 24px 80px',
          background: 'var(--g33-cream)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
            gap: 64,
            alignItems: 'center',
            maxWidth: 1240,
            margin: '0 auto',
          }}
          className="g33-hero-grid"
        >
          <div>
            <div className="g33-eyebrow" style={{ marginBottom: 20 }}>
              One-Day Live Online Cohort · Private Reference
            </div>
            <h1
              className="g33-display"
              style={{ fontSize: 60, lineHeight: 1.05, marginTop: 0, marginBottom: 24 }}
            >
              Gate 33 — Catering &amp; Operations Training
            </h1>
            <p className="g33-body" style={{ fontSize: 20, color: 'var(--g33-grey-warm)' }}>
              Ten steps, one continuous sequence. The full trainer reference for the
              live online cohort — objective, narrative, insight, interaction,
              demonstration, and key takeaways for every step.
            </p>
          </div>

          <div
            data-testid="catering-ref-hero-image"
            style={{
              aspectRatio: '4 / 5',
              width: '100%',
              overflow: 'hidden',
              border: '1px solid var(--g33-ligne)',
              background: 'var(--g33-cream-soft)',
            }}
          >
            <img
              src={CATERING_REF_HERO_IMG}
              alt="Gate 33 Catering & Operations Training — private aviation cabin reference"
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          </div>
        </div>
      </section>

      {/* Step index */}
      <section
        style={{
          padding: '48px 24px',
          background: 'var(--g33-cream-soft)',
          borderTop: '1px solid var(--g33-ligne)',
          borderBottom: '1px solid var(--g33-ligne)',
        }}
      >
        <div style={{ maxWidth: 860, margin: '0 auto' }}>
          <div className="g33-eyebrow" style={{ marginBottom: 20 }}>Index</div>
          <ol style={{ listStyle: 'none', padding: 0, margin: 0, columnCount: 1 }}>
            {CATERING_REF_STEPS.map((s) => (
              <li key={s.n} style={{ padding: '12px 0', borderBottom: '1px solid var(--g33-ligne)' }}>
                <a
                  href={`#step-${s.n}`}
                  data-testid={`catering-ref-index-${s.n}`}
                  className="g33-body"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '60px 1fr auto',
                    gap: 24,
                    alignItems: 'baseline',
                    textDecoration: 'none',
                    color: 'var(--g33-noir)',
                  }}
                >
                  <span
                    className="g33-eyebrow"
                    style={{ color: 'var(--g33-or)', fontSize: 12 }}
                  >
                    {s.n}
                  </span>
                  <span style={{ fontSize: 16 }}>{s.title}</span>
                  <span
                    className="g33-eyebrow"
                    style={{ color: 'var(--g33-grey-warm)', fontSize: 10 }}
                  >
                    {s.duration}
                  </span>
                </a>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Ten step blocks in a single continuous sequence */}
      {CATERING_REF_STEPS.map((step, idx) => (
        <StepBlock key={step.n} step={step} index={idx} total={total} />
      ))}

      <footer
        style={{
          borderTop: '1px solid var(--g33-ligne)',
          padding: '48px 16px',
          textAlign: 'center',
          background: 'var(--g33-cream)',
        }}
      >
        <p className="g33-eyebrow" style={{ color: 'var(--g33-grey-warm)' }}>
          Gate 33 — Private trainer reference · Not for distribution
        </p>
      </footer>
    </div>
  );
};

const Inner = () => {
  const { authed, checking } = useProgram();

  useEffect(() => {
    // Block search engines
    const tag = document.createElement('meta');
    tag.name = 'robots';
    tag.content = 'noindex, nofollow';
    document.head.appendChild(tag);
    // Load Playfair + Inter
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href =
      'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Playfair+Display:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400;1,600&display=swap';
    document.head.appendChild(link);
    const prevTitle = document.title;
    document.title = 'Gate 33 — Catering & Ops · Confidential';
    return () => {
      try { document.head.removeChild(tag); } catch {}
      try { document.head.removeChild(link); } catch {}
      document.title = prevTitle;
    };
  }, []);

  if (checking) {
    return (
      <div
        className="g33-program"
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div className="g33-eyebrow">Loading…</div>
      </div>
    );
  }

  if (!authed) return <PasswordGate />;
  return <Landing />;
};

const Gate33CateringRef = () => (
  <ProgramProvider>
    <Inner />
  </ProgramProvider>
);

export default Gate33CateringRef;
